/**
 * Jedno źródło prawdy: busy / spinner przy dłuższych akcjach
 * (potwierdzenie przyjęcia, GLS, wysyłka maila itd.).
 *
 * API: window.uiBusyIndicator
 *   - setElementBusy / clearElementBusy  — przycisk/link
 *   - showSwalLoading                    — dialog ze spinnerem
 *   - confirmThenBusy(swalOpts, work)    — Swal.confirm → spinner → work()
 *   - afterConfirmShowBusy(result, work) — gdy Swal.fire już wywołane
 *   - initBusyOnClick                    — klasy .js-busy-on-click / .js-busy-on-submit
 *
 * Tytuł loadera: argument → window.uiBusyIndicatorDefaultTitle (ustawiany w Blade).
 */

function resolveLoadingTitle(title) {
    if (title !== undefined && title !== null && String(title) !== '') {
        return String(title);
    }
    return window.uiBusyIndicatorDefaultTitle || '';
}

function spinnerHtml(label) {
    const text = label ? String(label) : '';
    return (
        '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>' +
        text
    );
}

/**
 * Blokuje element i pokazuje spinner. Zwraca false, jeśli już był busy.
 *
 * @param {HTMLElement|null} el
 * @param {string} [label]
 * @returns {boolean}
 */
function setElementBusy(el, label) {
    if (!el || el.dataset.busy === '1') {
        return false;
    }

    el.dataset.busy = '1';
    el.dataset.busyOriginalHtml = el.innerHTML;
    el.dataset.busyWasDisabled = el.disabled ? '1' : '0';

    if (el.tagName === 'BUTTON' || (el.tagName === 'INPUT' && el.type === 'submit')) {
        el.disabled = true;
    }

    if (el.tagName === 'A') {
        el.setAttribute('aria-disabled', 'true');
        el.classList.add('disabled', 'pe-none');
    }

    const busyLabel = label || el.dataset.busyLabel || '';
    el.innerHTML = spinnerHtml(busyLabel);

    return true;
}

/**
 * Przywraca element po setElementBusy (np. po fetch GUS).
 *
 * @param {HTMLElement|null} el
 */
function clearElementBusy(el) {
    if (!el || el.dataset.busy !== '1') {
        return;
    }

    el.innerHTML = el.dataset.busyOriginalHtml || '';
    el.disabled = el.dataset.busyWasDisabled === '1';
    el.classList.remove('disabled', 'pe-none');
    el.removeAttribute('aria-disabled');
    delete el.dataset.busy;
    delete el.dataset.busyOriginalHtml;
    delete el.dataset.busyWasDisabled;
}

/**
 * Blokuje inne przyciski/linki w tym samym kontenerze (np. footer karty).
 *
 * @param {HTMLElement} el
 */
function disableSiblingActions(el) {
    const scope = el.closest('.card-footer, form, .service-order-form-actions, .btn-toolbar') || el.parentElement;
    if (!scope) {
        return;
    }

    scope.querySelectorAll('a.btn, button, input[type="submit"]').forEach((other) => {
        if (other === el) {
            return;
        }
        if (other.tagName === 'BUTTON' || (other.tagName === 'INPUT' && other.type === 'submit')) {
            other.disabled = true;
        }
        other.classList.add('disabled', 'pe-none');
        other.setAttribute('aria-disabled', 'true');
    });
}

/**
 * SweetAlert ze spinnerem — użytkownik widzi, że operacja trwa.
 *
 * @param {string} [title]
 */
function showSwalLoading(title) {
    if (!window.Swal) {
        return;
    }

    const resolved = resolveLoadingTitle(title);

    window.Swal.fire({
        title: resolved || undefined,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
            window.Swal.showLoading();
        },
    });
}

/**
 * Po potwierdzeniu Swal: spinner, potem work(); na koniec zamyka Swal (gdy work to Promise).
 *
 * @param {import('sweetalert2').SweetAlertResult} result
 * @param {() => (void|Promise<unknown>)} onConfirm
 * @param {string} [loadingTitle]
 * @returns {Promise<unknown>}
 */
function afterConfirmShowBusy(result, onConfirm, loadingTitle) {
    if (!result || !result.isConfirmed) {
        return Promise.resolve();
    }

    showSwalLoading(loadingTitle);

    return Promise.resolve()
        .then(() => onConfirm())
        .catch((err) => {
            if (window.Swal) {
                window.Swal.close();
            }
            throw err;
        })
        .finally(() => {
            // Nawigacja / pełny submit zostawia spinner do przeładowania strony.
            // Dla Livewire / fetch zamykamy po zakończeniu work().
        });
}

/**
 * Jak afterConfirmShowBusy, ale zawsze zamyka Swal po zakończeniu work() (Livewire / mail).
 *
 * @param {import('sweetalert2').SweetAlertResult} result
 * @param {() => (void|Promise<unknown>)} onConfirm
 * @param {string} [loadingTitle]
 * @returns {Promise<unknown>}
 */
function runConfirmedBusy(result, onConfirm, loadingTitle) {
    if (!result || !result.isConfirmed) {
        return Promise.resolve();
    }

    showSwalLoading(loadingTitle);

    return Promise.resolve()
        .then(() => onConfirm())
        .finally(() => {
            if (window.Swal) {
                window.Swal.close();
            }
        });
}

/**
 * Swal.fire(options) → po confirm spinner + work().
 *
 * @param {import('sweetalert2').SweetAlertOptions} swalOptions
 * @param {() => (void|Promise<unknown>)} onConfirm
 * @param {string} [loadingTitle]
 * @returns {Promise<unknown>}
 */
function confirmThenBusy(swalOptions, onConfirm, loadingTitle) {
    if (!window.Swal) {
        return Promise.resolve(onConfirm());
    }

    return window.Swal.fire(swalOptions).then((result) =>
        afterConfirmShowBusy(result, onConfirm, loadingTitle)
    );
}

/**
 * Formularz: po submit przycisk → spinner + data-busy-label.
 *
 * @param {HTMLFormElement} form
 */
function bindBusyOnSubmit(form) {
    if (!form || form.dataset.busyBound === '1') {
        return;
    }
    // Formularz z własnym spinnerem / walidacją przed submit (np. unregistered-create).
    if (form.dataset.busyManual === '1') {
        return;
    }
    form.dataset.busyBound = '1';

    form.addEventListener('submit', () => {
        const btn =
            form.querySelector('.js-busy-submit-btn') ||
            form.querySelector('button[type="submit"]') ||
            form.querySelector('input[type="submit"]');
        if (btn) {
            setElementBusy(btn, btn.dataset.busyLabel);
            disableSiblingActions(btn);
        }
    });
}

/**
 * @param {ParentNode} [root]
 */
function initBusyOnClick(root = document) {
    root.querySelectorAll('.js-busy-on-click').forEach((el) => {
        if (el.dataset.busyBound === '1') {
            return;
        }
        el.dataset.busyBound = '1';

        el.addEventListener('click', (event) => {
            if (el.dataset.busy === '1') {
                event.preventDefault();
                return;
            }

            if (el.tagName === 'A' && el.getAttribute('href')) {
                event.preventDefault();
                if (!setElementBusy(el, el.dataset.busyLabel)) {
                    return;
                }
                disableSiblingActions(el);
                window.location.href = el.href;
                return;
            }

            if (!setElementBusy(el, el.dataset.busyLabel)) {
                event.preventDefault();
                return;
            }
            disableSiblingActions(el);
        });
    });

    root.querySelectorAll('form.js-busy-on-submit').forEach(bindBusyOnSubmit);

    // Przycisk z .js-busy-submit-btn (np. Turnstile / auth) — bez klasy na <form>.
    root.querySelectorAll('.js-busy-submit-btn').forEach((btn) => {
        bindBusyOnSubmit(btn.closest('form'));
    });
}

module.exports = {
    spinnerHtml,
    setElementBusy,
    clearElementBusy,
    disableSiblingActions,
    showSwalLoading,
    afterConfirmShowBusy,
    runConfirmedBusy,
    confirmThenBusy,
    initBusyOnClick,
};
